import * as React from 'react'
import styled from 'styled-components'
import { Visible, config } from 'react-awesome-styled-grid'
import { colors } from '@components/shared/colors'
import { P } from '@components/shared/typography'
import { AudioPlayer } from '@components/shared/AudioPlayer'
import { FadeInImage } from '@components/shared/FadeInImage'
import { getBackground } from './helpers'
import type { BackgroundType } from './helpers'
import type {
  BackgroundColorType,
  CtaDataType
} from '../../services/contentfulMarketingLandingPages'
import SharedCta from './SharedCta'

type PropsType = {
  pageId: string,
  title: string,
  audioUrl: string,
  backgroundColorText: BackgroundColorType,
  isMiniPlayerVisible: boolean,
  cta: CtaDataType | null
}

export default function BottomBanner (props: PropsType): React.ReactNode {
  const playerBackground: BackgroundType = getBackground(props.backgroundColorText, null)

  if (props.audioUrl) {
    const url = `https:${props.audioUrl}`
    const [currentTime, setCurrentTime] = React.useState('0:00')
    const [duration, setDuration] = React.useState('-:--')
    const [isPlaying, setIsPlaying] = React.useState(false)
    const toggleAudio = (): void => setIsPlaying(!isPlaying)

    return (
      <>
        <AudioPlayer
          isPlaying={isPlaying}
          src={url}
          onCurrentTimeChange={(formattedTime: string): void =>
            setCurrentTime(formattedTime)
          }
          onDurationSet={(formattedTime: string): void =>
            setDuration(formattedTime)
          }
          onAudioEnded={(): void => {
            setCurrentTime('0:00')
            setIsPlaying(false)
          }}
        />

        <BannerContainer
          backgroundColor={playerBackground.backgroundColor}
          isMiniPlayerVisible={props.isMiniPlayerVisible}
        >
          <Row>
            <PlayPauseButton onClick={toggleAudio} isPlaying={isPlaying} />
            <TextContainer>
              <TitleContainer>
                <Title>{props.title}</Title>
              </TitleContainer>
              <Time>{currentTime}</Time>
              <Divider>|</Divider>
              <Time alignRight>{duration}</Time>
            </TextContainer>

            <Visible md lg xl>
              {!!props.cta && <SharedCta cta={props.cta} />}
            </Visible>
          </Row>

          {playerBackground.backgroundImageUrl && (
            <BackgroundImageContainer>
              <FadeInImage
                src={playerBackground.backgroundImageUrl}
                height={playerBackground.height}
                width={playerBackground.width}
                ariaHidden={true}
              />
            </BackgroundImageContainer>
          )}
        </BannerContainer>
      </>
    )
  } else {
    return (
      <BannerContainer
        backgroundColor={playerBackground.backgroundColor}
        isMiniPlayerVisible={props.isMiniPlayerVisible}
      >
        <BannerInner>
          <BannerTextContainer>
            <BannerText>
              {props.title}
            </BannerText>
          </BannerTextContainer>

          {!!props.cta && (
            <>
              <Visible xs sm>
                <SharedCta cta={props.cta} isSmall />
              </Visible>

              <Visible md lg xl>
                <SharedCta cta={props.cta} />
              </Visible>
            </>
          )}
        </BannerInner>

        {playerBackground.backgroundImageUrl && (
          <BackgroundImageContainer>
            <FadeInImage
              src={playerBackground.backgroundImageUrl}
              height={playerBackground.height}
              width={playerBackground.width}
              ariaHidden={true}
            />
          </BackgroundImageContainer>
        )}
      </BannerContainer>
    )
  }
}

const BannerInner = styled.div<any>`
  align-items: center;
  position: relative;
  display: flex;
  height: 100%;
  justify-content: space-between;
  z-index: 2;

  margin: 0 20px;
  ${(props: any): any => config(props).media.md`
    margin: 0 60px;
  `}
`
const BannerTextContainer = styled.div<any>`
  max-width: 50%;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`

const BannerText = styled.p<any>`
  color: ${colors.white};
  font-family: 'Good Sans Regular';
  margin: 0;
  padding: 0;

  font-size: 12px;
  line-height: 18px;
  ${(props: any): any => config(props).media.md`
    font-size: 16px;
    line-height: 24px;
  `}
`

const PlayPauseButton = (props: {
  onClick: () => void,
  isPlaying: boolean
}): React.ReactNode => {
  return (
    <CircularButton onClick={props.onClick}>
      {props.isPlaying ? <PauseIcon /> : <PlayIcon />}
    </CircularButton>
  )
}

const CircularButton = styled.button<any>`
  align-items: center;
  background-color: ${colors.white};
  border: none;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  height: 50px;
  margin: 0;
  min-height: 50px;
  min-width: 50px;
  justify-content: center;
  outline: none;
  padding: 0;
  width: 50px;
  transition: all 0.3s;
  :hover,
  :focus-visible {
    background-color: ${colors.orange};
    svg {
      path {
        fill: ${colors.white};
      }
      rect {
        fill: ${colors.white};
      }
    }
  }
`

const PauseIcon = (): React.ReactNode => {
  return (
    <svg
      width='22'
      height='22'
      viewBox='0 0 21 21'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <rect
        x='4'
        y='1'
        width='5'
        height='18.75'
        rx='1.25'
        fill={colors.orange}
      />
      <rect
        x='12.75'
        y='1'
        width='5'
        height='18.75'
        rx='1.25'
        fill={colors.orange}
      />
    </svg>
  )
}

const PlayIcon = (): React.ReactNode => {
  return (
    <svg
      width='22'
      height='22'
      viewBox='0 0 21 21'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M20.7769 10.3885L5.19409 19.3852L5.19409 1.3918L20.7769 10.3885Z'
        fill={colors.orange}
      />
    </svg>
  )
}

const BannerContainer = styled.div<any>`
  background-color: ${(props: { backgroundColor: string }): string =>
    props.backgroundColor || colors.midnightBlue};
  border-top: 1px solid ${colors.white25};
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  z-index: 5;

  height: 80px;
  ${(props: any): any => config(props).media.md`
    height: 90px;
  `}

  ${(props: { isMiniPlayerVisible: boolean }): string | null | undefined => {
    if (props.isMiniPlayerVisible) {
      return `
        animation: open 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
        @keyframes open {0% {transform: translateY(100px);} 100% {transform: translateY(0);};
      `
    } else {
      return `
        animation: close 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
        @keyframes close {0% {transform: translateY(0);} 100% {transform: translateY(100px);};
      `
    }
  }}
`
const Row = styled.div<any>`
  align-items: center;
  position: relative;
  display: flex;
  height: 100%;
  z-index: 2;

  margin: 0 20px;
  ${(props: any): any => config(props).media.md`
    margin: 0 60px;
  `}
`
const BackgroundImageContainer = styled.div<any>`
  height: auto;
  left: 0;
  position: absolute;
  top: 0;
`
const TextContainer = styled.div<any>`
  width: 100%;

  margin: 0 0 0 20px;
  ${(props: any): any => config(props).media.md`
    margin: 0 15px 0 32px;
  `}
`
const TitleContainer = styled.div<any>`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`
const Title = styled.p<any>`
  color: ${colors.white};
  font-family: 'Queens Condensed Regular';
  margin: 0;
  padding: 0;

  font-size: 23px;
  line-height: 25px;
  ${(props: any): any => config(props).media.md`
    font-size: 36px;
    line-height: 40px;
  `}
`
const Time = styled(P)<any>`
  color: ${colors.white};
  display: inline-block;
  font-size: 11px;
  letter-spacing: 0.08em;
  line-height: 13px;
  min-width: 32px;
  text-align: ${(props: { alignRight: boolean }): string =>
    props.alignRight ? 'right' : 'left'};
  text-transform: uppercase;
`
const Divider = styled(Time)<any>`
  margin: 0 0 0 4px;
  min-width: unset;
`
